import React, { createContext, FC, PropsWithChildren, useReducer, useEffect, useContext, useCallback, useRef } from 'react';
import { Auth } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { message } from 'antd';
import { cloneDeep } from 'lodash';
import { useApolloClient } from 'react-apollo';
import { useHistory } from 'react-router-dom';
import { ThemeContext } from './theme';
import { AuthRefreshOptions, loadAuthenticatedUser, signOutAndClearPermissions } from './auth-session';

export interface AuthContextProps {
    user: any,
    loading: boolean,
    error: any,
    signOut: () => Promise<any>,
    refreshUser?: (options?: AuthRefreshOptions) => Promise<any>
}

export const AuthContext = createContext<AuthContextProps>({ user: null, loading: true, error: null, signOut: null, refreshUser: () => null })

const initialState = { user: null, loading: true }

function authReducer(state, action){
    switch (action.type) {
        case 'FETCHING':
            return { user: null, loading: true }
        case 'RECIEVED':
            return { user: action.payload, loading: false }
        case 'ERROR':
            return { user: null, error: action.payload, loading: false }
        case 'SIGNED_OUT':
            return { user: null, loading: false }
        case 'UPDATE_USER_ATTRIBUTES':
            if (!state.user) return state;

            const user = cloneDeep(state.user);
            Object.assign(user, { attributes: {
                ...state.user.attributes,
                ...action.payload
            } })
            if (action.payload.email !== state.user.attributes.email){
                // Email address was changed. Needs to turn off email_verified attribute.
                user.attributes.email_verified = false;
            }
            return { ...state, user };
        case 'VERIFY_EMAIL':
            let user_to_verify = { ...state.user }
            user_to_verify.attributes.email_verified = true;
            return { ...state, user: user_to_verify }
        case 'UNVERIFY_EMAIL':
            let user_to_unverify = { ...state.user }
            user_to_unverify.attributes.email_verified = false;
            return { ...state, user: user_to_unverify }
        default:
            break;
    }
}

export const AuthProvider: FC<PropsWithChildren<{}>> = (props) => {
    const [state, dispatch] = useReducer(authReducer, initialState);
    const apolloClient = useApolloClient();
    const history = useHistory();
    const theme = useContext(ThemeContext);
    const requestVersion = useRef(0);
    const mounted = useRef(true);
    const pendingLoad = useRef<Promise<any>>(null);
    const pendingForceRefresh = useRef(false);
    const signingOut = useRef(false);

    const loadUser = useCallback((options: AuthRefreshOptions = {}): Promise<any> => {
        if (signingOut.current) return Promise.reject(new Error('Logout is in progress'));
        if (pendingLoad.current) {
            // A startup attribute lookup cannot satisfy a requested token renewal.
            if (options.forceRefresh && !pendingForceRefresh.current) {
                return pendingLoad.current.then(() => loadUser(options));
            }
            return pendingLoad.current;
        }
        const version = ++requestVersion.current;
        const canApply = () => mounted.current && version === requestVersion.current;
        const request = loadAuthenticatedUser(apolloClient, options, canApply)
            .then(user => {
                if (!canApply()) throw new Error('Authentication request is no longer active');
                dispatch({ type: 'RECIEVED', payload: user });
                return user;
            })
            .catch(e => {
                if (canApply() && !options.forceRefresh) {
                    message.error("Could not retrieve login details");
                    dispatch({ type: 'ERROR', payload: e });
                }
                throw e;
            })
            .finally(() => {
                if (pendingLoad.current === request) pendingLoad.current = null;
            });
        pendingLoad.current = request;
        pendingForceRefresh.current = !!options.forceRefresh;
        return request;
    }, [apolloClient]);

    useEffect(() => {
        mounted.current = true;
        loadUser().catch(() => undefined);

        return () => {
            mounted.current = false;
            ++requestVersion.current;
        }
    }, [apolloClient, loadUser])

    useEffect(() => {
        function listener (data){
            if (!state.user) return;
            console.log('user channel event fired:', data, state);
            switch (data.payload.event) {
                case 'updateUserAttributes':
                    if (!state.user || !state.user.getSession) return;
                    const prevAttr = { ...state.user.attributes };
                    dispatch({ type: 'UPDATE_USER_ATTRIBUTES', payload: data.payload.data.attributes })
                    Auth.updateUserAttributes(state.user, data.payload.data.attributes)
                    .then(() => {
                        message.success(data.payload.data.customSuccessMessage || "Successfully updated user attributes");
                    })
                    .catch((e) => {
                        console.log('Failed to update user attributes:', e);
                        message.error("An error occurred while updating user attributes. Please try again later.");
                        dispatch({ type: 'UPDATE_USER_ATTRIBUTES', payload: prevAttr })
                    })
                    break;
                case 'verifyEmail':
                    Auth.verifyCurrentUserAttributeSubmit('email', data.payload.data.code)
                    .then(() => {
                        message.success("Successfully verified email address.")
                        dispatch({ type: 'VERIFY_EMAIL' });
                    })
                    .catch((err) => {
                        message.error('Failed to verify email address: ' + err.message);
                        console.error(err);
                    })
                    break;
            }
        }
        Hub.remove('user', listener);
        Hub.listen('user', listener);
        return () => {
            Hub.remove('user', listener);
        }
    // eslint-disable-next-line
    }, [state.user])

    return <AuthContext.Provider value={{
        ...state,
        refreshUser: loadUser,
        signOut: async () => {
            signingOut.current = true;
            ++requestVersion.current;
            theme.setThemingEnabled(false);
            try {
                // Let any token refresh finish before removing its tokens during logout.
                await pendingLoad.current?.catch(() => undefined);
                await signOutAndClearPermissions(apolloClient);
                dispatch({ type: 'SIGNED_OUT' });
                message.success('You have been logged out');
                history.push('/login');
            } catch (err) {
                theme.setThemingEnabled(true);
                message.error('Logout failed. Please try again.');
                throw err;
            } finally {
                signingOut.current = false;
            }
        }
    }}>
        {props.children}
    </AuthContext.Provider>
}
